/**
 * decoder-verify.js — pick a VideoDecoder configuration that ACTUALLY decodes
 * THIS file, by decoding one of its real keyframes.
 *
 * WHY THIS EXISTS
 * ---------------
 * VideoDecoder.isConfigSupported() is not an answer, it is an opinion, and on
 * Android it is a wrong one. Chromium there has no software H.264 decoder at
 * all (proprietary decode comes entirely from MediaCodec), yet
 * isConfigSupported({ hardwareAcceleration: 'prefer-software' }) still answers
 * supported:true — the field is specified as a HINT, so the probe reports on
 * the codec string and ignores it. configure() then accepts the config too,
 * and the failure arrives later, asynchronously, on the error callback as:
 *
 *     OperationError: Unsupported configuration. Check isConfigSupported()
 *                     prior to calling configure().
 *
 * ...which is the most misleading message in WebCodecs, because the caller DID
 * check isConfigSupported() and it said yes.
 *
 * Measured on prod (RU shard, 30 d to 2026-09-08), /reverse-video, split by
 * whether the chosen decoder was the hardware one:
 *
 *     device    decoder     runs   this failure   rate
 *     mobile    software     383       289        75.5 %
 *     mobile    hardware     357         2         0.6 %
 *     desktop   software     180         0         0.0 %
 *     desktop   hardware     795        31         3.9 %
 *
 * Desktop software decode never fails; mobile software decode fails three runs
 * in four. The only thing that differed was a hint ladder that put
 * 'prefer-software' first on Android. That ladder was a GUESS copied from the
 * ENCODER (where Android's MediaCodec really is unreliable) — the comment that
 * introduced it called it "a cheap safety margin". It cost 289 runs a
 * ~20-minute FFmpeg-WASM grind on a phone.
 *
 * The same root was already found and fixed once, on 2026-07-10, inside
 * webcodecs-pipeline.js (DECODER_HINTS + verifyDecoderFor). It stayed broken
 * here because the fix landed in one copy of the cascade and reverse-webcodecs
 * carried its own. This module is that fix with ONE owner, so the next module
 * does not get to make the mistake a third time.
 *
 * THE DOCTRINE
 * ------------
 * Earn the verdict by DOING. Configure a real decoder, feed it one real
 * keyframe from the user's own file, and demand one decoded frame back. That
 * is the same authority configure() holds at run time, bought for ~10-300 ms
 * instead of a doomed export. It is also the only test that is right in BOTH
 * directions: a wedged GPU process answers supported:false for codecs it
 * decoded yesterday, and Android answers supported:true for a decoder it does
 * not have.
 *
 * Hardware first on every platform: it is what every native player uses, and
 * the measurement above says software is the risky one, not the safe one.
 *
 * A TIMEOUT IS NOT A NO. A decoder that neither emits nor errors is a wedge we
 * cannot attribute; reporting it as a verified refusal would push a healthy
 * user onto the slow lane. `uncertain` keeps the caller's optimism.
 *
 * API — window.DecoderVerify:
 *
 *   DecoderVerify.HINTS                        // ['prefer-hardware','prefer-software',null]
 *
 *   await DecoderVerify.pick({
 *      codec, codedWidth, codedHeight,
 *      description,          // avcC / hvcC / av1C / vpcC bytes, optional
 *      keyframe: { data, timestamp, duration },   // a real 'key' sample
 *      timeoutMs,            // per hint; default 4000
 *      slug,                 // telemetry tag
 *   }) -> { ok, uncertain, config, hardware, hint, ms, reason }
 *
 *     ok:true        -> `config` decoded a real frame. Use it verbatim.
 *     uncertain:true -> no verdict (no keyframe, no WebCodecs, every hint timed
 *                      out). `config` is the best-effort first candidate; the
 *                      caller may still try it.
 *     both false     -> every hint refused cleanly. A verified no.
 *
 *   DecoderVerify.reset()    // test seam: drop the per-(codec@WxH) cache
 */
(function () {
'use strict';
if (window.DecoderVerify) return;

/* Hardware first on EVERY platform — see the header. The ladder ends with
   null (no hint at all) for implementations that want none. */
var HINTS = ['prefer-hardware', 'prefer-software', null];

var DEFAULT_TIMEOUT_MS = 4000;

/* Cached per (codec @ WxH): the verdict is a property of the device and the
   stream shape, not of the individual file. Promise-valued so two concurrent
   callers share one probe. */
var cache = Object.create(null);

/* Cheap content fingerprint of a codec description (avcC/hvcC/av1C/vpcC).
   Length plus an FNV-1a over the bytes — enough to tell two SPS/PPS records
   apart, and it never has to be reversible. */
function descFingerprint(desc) {
    if (!desc) return '0';
    var b;
    try { b = (desc instanceof Uint8Array) ? desc : new Uint8Array(desc.buffer || desc); }
    catch (_) { return 'x'; }
    var h = 0x811c9dc5;
    for (var i = 0; i < b.length; i++) {
        h ^= b[i];
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return b.length + '.' + h.toString(36);
}

function ae(payload) {
    try { window.__ae && window.__ae('feature_use', payload); } catch (_) {}
}

/* One hint, one real keyframe, one frame demanded back.
   Resolves { decoded, timedOut, err }. Never throws. */
function tryHint(cfg, keyframe, timeoutMs) {
    return new Promise(function (resolve) {
        var dec = null, frames = 0, settled = false;
        var timer = setTimeout(function () { finish(true, 'timeout'); }, timeoutMs);
        function finish(timedOut, err) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            try { if (dec && dec.state !== 'closed') dec.close(); } catch (_) {}
            resolve({ decoded: frames > 0, timedOut: !!timedOut && frames === 0, err: err || '' });
        }
        try {
            dec = new VideoDecoder({
                output: function (f) { frames++; try { f.close(); } catch (_) {} finish(false, ''); },
                error: function (e) { finish(false, String((e && e.name) || 'error') + ': ' + String((e && e.message) || e).slice(0, 90)); }
            });
            /* configure() can throw SYNCHRONOUSLY (driver-blacklisted codecs —
               HEVC on Linux, 10-bit on some Windows). That is a clean refusal
               for this hint, not for the file. */
            dec.configure(cfg);
            dec.decode(new EncodedVideoChunk({
                type: 'key',
                timestamp: keyframe.timestamp || 0,
                duration: keyframe.duration || 0,
                data: keyframe.data instanceof Uint8Array ? keyframe.data : new Uint8Array(keyframe.data)
            }));
            /* flush() is what forces a decoder holding one frame to emit it;
               without it an implementation that batches can sit on the single
               chunk until the timeout and read as a wedge. */
            dec.flush().then(
                function () { finish(false, frames > 0 ? '' : 'flushed-empty'); },
                function (e) { finish(false, 'flush: ' + String((e && e.message) || e).slice(0, 60)); }
            );
        } catch (syncErr) {
            finish(false, String((syncErr && syncErr.name) || 'throw') + ': ' + String((syncErr && syncErr.message) || syncErr).slice(0, 90));
        }
    });
}

async function pick(opts) {
    opts = opts || {};
    var codec = opts.codec;
    var base = { codec: codec, codedWidth: opts.codedWidth, codedHeight: opts.codedHeight };
    if (opts.description) base.description = opts.description;

    var fallback = { ok: false, uncertain: true, config: base, hardware: false, hint: '', ms: 0, reason: '' };
    if (!codec) { fallback.reason = 'no-codec'; return fallback; }
    if (typeof VideoDecoder === 'undefined' || typeof EncodedVideoChunk === 'undefined') {
        fallback.reason = 'no-webcodecs'; return fallback;
    }

    var kf = opts.keyframe;
    if (!kf || !kf.data) {
        /* No keyframe to try. Fall back to asking — an opinion beats nothing,
           and this is the one branch where it is all we have. */
        fallback.reason = 'no-keyframe';
        try {
            for (var i = 0; i < HINTS.length; i++) {
                var c = Object.assign({}, base);
                if (HINTS[i]) c.hardwareAcceleration = HINTS[i];
                var r = await VideoDecoder.isConfigSupported(c);
                if (r && r.supported) {
                    return { ok: false, uncertain: true, config: r.config || c,
                             hardware: HINTS[i] === 'prefer-hardware', hint: HINTS[i] || 'none',
                             ms: 0, reason: 'no-keyframe' };
                }
            }
        } catch (_) {}
        return fallback;
    }

    /* ⚠️ The cache key MUST include the description, not just codec@WxH.
       The RESULT carries a `config`, and that config carries THIS file's
       avcC/hvcC. Two different 1080p H.264 clips share a codec string and
       dimensions but not their SPS/PPS, so a key without the description would
       hand file B the decoder record of file A — a decode failure or, worse,
       a corrupt picture, on a path that reports success.
       (webcodecs-pipeline's verifyDecoderFor may key on codec@WxH because it
       returns only a verdict; the caller there builds its own config.) */
    var key = String(codec) + '@' + opts.codedWidth + 'x' + opts.codedHeight
            + '#' + descFingerprint(opts.description);
    if (cache[key]) return cache[key];

    cache[key] = (async function () {
        var t0 = Date.now();
        var timeoutMs = opts.timeoutMs > 0 ? opts.timeoutMs : DEFAULT_TIMEOUT_MS;
        var timedOutAnywhere = false, lastErr = '', first = null;
        for (var h = 0; h < HINTS.length; h++) {
            var cfg = Object.assign({}, base);
            if (HINTS[h]) cfg.hardwareAcceleration = HINTS[h];
            /* Ask first — it is free, and its NO is trustworthy (a refusal is
               never optimistic). Only its YES needs the real decode behind it. */
            var asked = true;
            try {
                var s = await VideoDecoder.isConfigSupported(cfg);
                asked = !!(s && s.supported);
                if (asked && s.config) cfg = s.config;
            } catch (_) { asked = false; }
            if (!asked) { lastErr = lastErr || 'probe-said-no'; continue; }
            if (!first) first = cfg;

            /* Test seam — return the defect. With this set, `pick` degrades to
               exactly what it replaced: trust the probe's yes and skip the real
               decode. The guard (decoder-verify-beats-a-lying-probe.mjs) uses it
               to prove the decode is what does the work, not the hint order.
               Guarded, so it is inert unless a test sets it. */
            if (window.__DV_FORCE_ASK_ONLY) {
                return { ok: true, uncertain: false, config: cfg,
                         hardware: HINTS[h] === 'prefer-hardware', hint: HINTS[h] || 'none',
                         ms: Date.now() - t0, reason: 'ask-only' };
            }

            var res = await tryHint(cfg, kf, timeoutMs);
            if (res.decoded) {
                var okOut = {
                    ok: true, uncertain: false, config: cfg,
                    hardware: HINTS[h] === 'prefer-hardware', hint: HINTS[h] || 'none',
                    ms: Date.now() - t0, reason: ''
                };
                ae({ slug: opts.slug || window.__TOOL_SLUG || 'unknown', f: 'dec_verify',
                     ok: 1, hint: okOut.hint, idx: h, ms: okOut.ms,
                     codec: String(codec).slice(0, 20), w: opts.codedWidth, h: opts.codedHeight });
                return okOut;
            }
            if (res.timedOut) timedOutAnywhere = true;
            lastErr = res.err || lastErr;
        }
        var out = {
            ok: false,
            /* Every candidate timed out -> no verdict; stay optimistic with the
               first config the probe accepted. Clean refusals -> a verified no. */
            uncertain: timedOutAnywhere,
            config: first || base,
            hardware: false, hint: '', ms: Date.now() - t0,
            reason: lastErr || 'all-hints-refused'
        };
        ae({ slug: opts.slug || window.__TOOL_SLUG || 'unknown', f: 'dec_verify',
             ok: 0, uncertain: out.uncertain ? 1 : 0, ms: out.ms,
             reason: String(out.reason).slice(0, 60),
             codec: String(codec).slice(0, 20), w: opts.codedWidth, h: opts.codedHeight });
        return out;
    })();
    return cache[key];
}

window.DecoderVerify = {
    HINTS: HINTS,
    pick: pick,
    reset: function () { cache = Object.create(null); }
};
})();
